import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { giaoDichApi, kiemTraDaChuong } from "api/giaoDich";
import { chuVuonApi } from "api/chuVuon";
import { nganHangApi } from "api/nganHang";
import { useAuth } from "context/AuthContext";
import { MAU, FONT_HIEU, FONT_CHU } from "assets/theme/avocadoBrand";
import { nhanTrangThaiThanhToan } from "lib/nhanTrangThaiThanhToan";
import { phatTinhTinh } from "lib/amThanhThongBao";
import {
  timNganHangTheoTen,
  taoUrlAnhVietQr,
  taoNoiDungChuyenKhoanKhongDau,
} from "lib/vietQrChuyenKhoan";
import useTour from "hooks/useTour";
import buocXacNhanThanhToan from "tours/xacNhanThanhToan";

function Nhan({ children }) {
  return (
    <MDTypography
      sx={{
        display: "block",
        mb: 0.25,
        fontFamily: FONT_CHU,
        fontWeight: 600,
        fontSize: "0.65rem",
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: alpha(MAU.ink, 0.55),
      }}
    >
      {children}
    </MDTypography>
  );
}

Nhan.propTypes = {
  children: PropTypes.node.isRequired,
};

// Ô thông tin gọn: "Nhãn: giá trị" trên cùng 1 dòng, dùng lại cho cả phiếu
// kiểm tra lẫn khối chuyển khoản.
function Don({ nhan, giaTri, xs }) {
  return (
    <Grid item xs={xs}>
      <MDBox sx={{ wordBreak: "break-word" }}>
        <MDTypography
          component="span"
          sx={{
            fontFamily: FONT_CHU,
            fontWeight: 600,
            fontSize: "0.68rem",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: alpha(MAU.ink, 0.55),
          }}
        >
          {nhan}:{" "}
        </MDTypography>
        <MDTypography
          component="span"
          sx={{
            fontFamily: FONT_HIEU,
            fontWeight: 600,
            fontSize: "0.95rem",
            color: MAU.ink,
          }}
        >
          {giaTri ?? "—"}
        </MDTypography>
      </MDBox>
    </Grid>
  );
}

Don.propTypes = {
  nhan: PropTypes.string.isRequired,
  giaTri: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  xs: PropTypes.number,
};

Don.defaultProps = {
  giaTri: null,
  xs: 6,
};

// Ngân hàng đòi dán riêng từng ô (số tài khoản, số tiền, nội dung) — mỗi
// dòng có nút chép riêng thay vì 1 khối chép hết 1 lượt.
function HangSaoChep({ nhan, hienThi, giaTriCopy, id, dangCopyId, onSaoChep }) {
  const daCopy = dangCopyId === id;
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1} py={0.5}>
      <MDBox sx={{ minWidth: 0 }}>
        <Nhan>{nhan}</Nhan>
        <MDTypography
          sx={{
            fontFamily: FONT_CHU,
            fontWeight: 600,
            fontSize: "0.85rem",
            color: MAU.ink,
            wordBreak: "break-word",
          }}
        >
          {hienThi}
        </MDTypography>
      </MDBox>
      <MDButton
        variant="text"
        color={daCopy ? "success" : "dark"}
        size="small"
        onClick={() => onSaoChep(giaTriCopy, id)}
        sx={{ flexShrink: 0, fontFamily: FONT_CHU }}
      >
        <Icon sx={{ mr: 0.5 }} fontSize="small">
          {daCopy ? "check" : "content_copy"}
        </Icon>
        {daCopy ? "Đã chép" : "Chép"}
      </MDButton>
    </MDBox>
  );
}

HangSaoChep.propTypes = {
  nhan: PropTypes.string.isRequired,
  hienThi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  giaTriCopy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  id: PropTypes.string.isRequired,
  dangCopyId: PropTypes.string,
  onSaoChep: PropTypes.func.isRequired,
};

HangSaoChep.defaultProps = {
  dangCopyId: null,
};

// 1 dòng/loại bơ, gọn trên 1 hàng (tên + khối lượng × đơn giá bên trái,
// thành tiền bên phải) thay vì lưới 4 ô rời rạc như trước.
function DongLoaiBo({ ten, khoiLuong, donGia, thanhTien, maSotHienThi, coVachTren }) {
  return (
    <MDBox
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 1,
        py: 0.75,
        borderTop: coVachTren ? `1px dashed ${alpha(MAU.ink, 0.15)}` : "none",
      }}
    >
      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography
          sx={{ fontFamily: FONT_CHU, fontWeight: 600, fontSize: "0.85rem", color: MAU.ink }}
        >
          {ten}
        </MDTypography>
        <MDTypography
          sx={{ fontFamily: FONT_CHU, fontSize: "0.72rem", color: alpha(MAU.ink, 0.55) }}
        >
          {khoiLuong} kg × {donGia.toLocaleString("vi-VN")} đ/kg
          {maSotHienThi ? ` · Sọt: ${maSotHienThi}` : ""}
        </MDTypography>
      </MDBox>
      <MDTypography
        sx={{
          fontFamily: FONT_HIEU,
          fontWeight: 600,
          fontSize: "1rem",
          color: MAU.skin,
          flexShrink: 0,
        }}
      >
        {thanhTien.toLocaleString("vi-VN")} đ
      </MDTypography>
    </MDBox>
  );
}

DongLoaiBo.propTypes = {
  ten: PropTypes.string.isRequired,
  khoiLuong: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  donGia: PropTypes.number.isRequired,
  thanhTien: PropTypes.number.isRequired,
  maSotHienThi: PropTypes.string,
  coVachTren: PropTypes.bool.isRequired,
};

DongLoaiBo.defaultProps = {
  maSotHienThi: null,
};

function XacNhanThanhToan() {
  const { id } = useParams();
  const { user } = useAuth();

  const [giaoDich, setGiaoDich] = useState(null);
  const [chuVuon, setChuVuon] = useState(null);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState("");

  const [daGuiKeToan, setDaGuiKeToan] = useState(false);
  const [daCopyId, setDaCopyId] = useState(null);
  const [dangLuu, setDangLuu] = useState(false);
  const [dangChuong, setDangChuong] = useState(false);
  const [thongBaoChuong, setThongBaoChuong] = useState("");
  const [danhSachNganHang, setDanhSachNganHang] = useState([]);

  useEffect(() => {
    nganHangApi
      .danhSach()
      .then(setDanhSachNganHang)
      .catch(() => setDanhSachNganHang([]));
  }, []);

  useEffect(() => {
    setDangTai(true);
    setLoi("");
    setChuVuon(null);

    const huyGd = giaoDichApi.langNgheChiTiet(
      id,
      (gd) => {
        setGiaoDich(gd);
        setDaGuiKeToan(gd?.trangThaiThanhToan === "cho_ke_toan");
        setDangTai(false);
      },
      (err) => {
        setLoi(err.message);
        setDangTai(false);
      }
    );
    return huyGd;
  }, [id]);

  useEffect(() => {
    if (!giaoDich?.maChuVuon) {
      setChuVuon(null);
      return undefined;
    }
    const huy = chuVuonApi.langNgheChiTiet(giaoDich.maChuVuon, setChuVuon);
    return huy;
  }, [giaoDich?.maChuVuon]);

  const tongThanhTien = useMemo(
    () => (giaoDich ? giaoDich.items.reduce((tong, it) => tong + it.thanhTien, 0) : 0),
    [giaoDich]
  );

  // Chủ vườn lưu địa chỉ theo từng phần (chi tiết/thôn/xã/tỉnh) chứ không có
  // sẵn 1 chuỗi đầy đủ — ghép lại để hiện gọn 1 dòng, giống FormThuMua.
  const diaChiChuVuon = useMemo(() => {
    if (!chuVuon) return "";
    return [chuVuon.diaChi, chuVuon.thon, chuVuon.xa, chuVuon.tinh].filter(Boolean).join(", ");
  }, [chuVuon]);

  const laNguoiTao = Boolean(user?.uid && giaoDich?.maNhanVien === user.uid);
  const conChoXuLy = giaoDich?.trangThaiThanhToan === "cho_xu_ly";
  const coTheGuiKeToan = laNguoiTao && conChoXuLy && !daGuiKeToan;
  const nhanTrangThai = nhanTrangThaiThanhToan(giaoDich?.trangThaiThanhToan);

  const noiDungCk = useMemo(() => {
    if (!giaoDich) return "";
    return taoNoiDungChuyenKhoanKhongDau({
      tenChuVuon: giaoDich.tenChuVuon,
      thoiGian: giaoDich.thoiGian,
    });
  }, [giaoDich]);

  const nganHangKhop = useMemo(
    () => timNganHangTheoTen(chuVuon?.tenNganHang, danhSachNganHang),
    [chuVuon?.tenNganHang, danhSachNganHang]
  );

  const urlQrCk = useMemo(() => {
    if (!chuVuon?.soTkNganHang || !nganHangKhop?.bin) return "";
    return taoUrlAnhVietQr({
      bin: nganHangKhop.bin,
      soTk: chuVuon.soTkNganHang,
      soTien: tongThanhTien,
      noiDung: noiDungCk,
      tenChuTk: chuVuon.chuTaiKhoan || chuVuon.hoTen || "",
    });
  }, [chuVuon, nganHangKhop, tongThanhTien, noiDungCk]);

  async function saoChep(giaTri, copyId) {
    try {
      await navigator.clipboard.writeText(String(giaTri));
      setDaCopyId(copyId);
      setTimeout(() => setDaCopyId((cur) => (cur === copyId ? null : cur)), 1500);
    } catch {
      setLoi("Không sao chép được — trình duyệt chặn quyền clipboard.");
    }
  }

  async function guiVeKeToan() {
    setLoi("");
    // Phát ngay trong lượt click.
    phatTinhTinh("yeu_cau");
    setDangLuu(true);
    try {
      await giaoDichApi.guiKeToan(id);
      setDaGuiKeToan(true);
      setGiaoDich((gd) => (gd ? { ...gd, trangThaiThanhToan: "cho_ke_toan" } : gd));
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuu(false);
    }
  }

  async function chuongNhacKeToan() {
    setLoi("");
    setThongBaoChuong("");
    const check = kiemTraDaChuong(giaoDich?.thoiGianChuongKeToan);
    if (!check.ok) {
      setLoi(check.message);
      return;
    }
    // Chỉ kêu khi đã qua cooldown.
    phatTinhTinh("chuong");
    setDangChuong(true);
    try {
      const { thoiGianChuongKeToan } = await giaoDichApi.chuongNhacKeToan(id);
      setGiaoDich((gd) => (gd ? { ...gd, thoiGianChuongKeToan } : gd));
      setThongBaoChuong("Đã chuông nhắc kế toán.");
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangChuong(false);
    }
  }

  useTour(
    "thu-mua-xac-nhan-thanh-toan",
    dangTai || !giaoDich || daGuiKeToan || !coTheGuiKeToan ? [] : buocXacNhanThanhToan()
  );

  if (dangTai) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={{ xs: 1.5, md: 3 }}>
          <MDTypography variant="button" color="text">
            Đang tải...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!giaoDich) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={{ xs: 1.5, md: 3 }}>
          <MDAlert color="error">{loi || "Không tìm thấy giao dịch."}</MDAlert>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1.5, md: 3 }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={7} lg={5}>
            <Card sx={{ mb: 2, borderRadius: "16px" }}>
              <MDBox p={2.5}>
                <MDTypography
                  sx={{
                    fontFamily: FONT_HIEU,
                    fontWeight: 600,
                    fontSize: "1.35rem",
                    color: MAU.ink,
                    mb: 0.5,
                  }}
                >
                  Gửi về kế toán
                </MDTypography>
                <MDTypography
                  sx={{
                    fontFamily: FONT_CHU,
                    fontSize: "0.8rem",
                    color: alpha(MAU.ink, 0.6),
                    mb: 1.5,
                  }}
                >
                  Trạng thái: {nhanTrangThai.label}
                </MDTypography>

                {conChoXuLy && giaoDich.ghiChuThanhToan && (
                  <MDBox mb={1.5}>
                    <MDAlert color="warning">Kế toán trả lại: {giaoDich.ghiChuThanhToan}</MDAlert>
                  </MDBox>
                )}

                <Grid container rowSpacing={0.5} columnSpacing={1.5}>
                  <Don nhan="Chủ vườn" giaTri={giaoDich.tenChuVuon} xs={12} />
                  <Don nhan="Điện thoại" giaTri={chuVuon?.sdt} xs={12} />
                  <Don nhan="Vườn" giaTri={giaoDich.tenVuon} xs={12} />
                  <Don nhan="Địa chỉ" giaTri={diaChiChuVuon || null} xs={12} />
                  {giaoDich.maHopTacXa && (
                    <Don nhan="Hợp tác xã" giaTri={giaoDich.tenHopTacXa} xs={12} />
                  )}
                </Grid>

                <MDBox sx={{ mt: 1.5 }}>
                  {giaoDich.items.map((it, idx) => (
                    <DongLoaiBo
                      key={idx}
                      ten={it.tenLoaiBo}
                      khoiLuong={it.khoiLuong}
                      donGia={it.donGia}
                      thanhTien={it.thanhTien}
                      maSotHienThi={it.maSotHienThi}
                      coVachTren={idx > 0}
                    />
                  ))}
                </MDBox>

                {giaoDich.items.length > 1 && (
                  <MDBox
                    sx={{
                      mt: 0.5,
                      pt: 1.5,
                      borderTop: `1.5px dashed ${alpha(MAU.ink, 0.18)}`,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <MDTypography
                      sx={{
                        fontFamily: FONT_CHU,
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: alpha(MAU.ink, 0.6),
                      }}
                    >
                      Tổng thành tiền
                    </MDTypography>
                    <MDTypography
                      sx={{
                        fontFamily: FONT_HIEU,
                        fontWeight: 600,
                        fontSize: "1.4rem",
                        color: MAU.skin,
                      }}
                    >
                      {tongThanhTien.toLocaleString("vi-VN")} đ
                    </MDTypography>
                  </MDBox>
                )}

                {coTheGuiKeToan && (
                  <MDButton
                    component={Link}
                    to={`/thu-mua/${giaoDich.maChuVuon}/${giaoDich.maVuon}/nhap`}
                    state={{ suaPOId: id }}
                    variant="text"
                    color="dark"
                    size="small"
                    fullWidth
                    sx={{ mt: 1.5, fontFamily: FONT_CHU, fontWeight: 600 }}
                  >
                    <Icon sx={{ mr: 0.5 }} fontSize="small">
                      arrow_back
                    </Icon>
                    Quay lại sửa PO
                  </MDButton>
                )}
              </MDBox>
            </Card>

            {coTheGuiKeToan && (
              <Card sx={{ borderRadius: "16px" }}>
                <MDBox p={2.5}>
                  <MDTypography
                    sx={{
                      fontFamily: FONT_HIEU,
                      fontWeight: 600,
                      fontSize: "1.2rem",
                      color: MAU.ink,
                      mb: 1.5,
                    }}
                  >
                    Thông tin chuyển khoản (tham khảo)
                  </MDTypography>
                  {!chuVuon?.tenNganHang && (
                    <MDBox mb={2}>
                      <MDAlert color="warning">
                        Chủ vườn này chưa có thông tin ngân hàng — hãy hỏi trực tiếp hoặc bổ sung ở
                        trang Quản lý Chủ vườn / Vườn trước khi gửi kế toán.
                      </MDAlert>
                    </MDBox>
                  )}
                  {chuVuon?.tenNganHang && !chuVuon?.soTkNganHang && (
                    <MDBox mb={2}>
                      <MDAlert color="warning">Thiếu số tài khoản — không tạo được mã QR.</MDAlert>
                    </MDBox>
                  )}
                  {chuVuon?.tenNganHang && chuVuon?.soTkNganHang && !nganHangKhop?.bin && (
                    <MDBox mb={2}>
                      <MDAlert color="warning">
                        Không map được mã ngân hàng (BIN) — vẫn có thể chép tay STK / số tiền.
                      </MDAlert>
                    </MDBox>
                  )}

                  <MDBox
                    id="tour-xac-nhan-chuyen-khoan"
                    p={1.5}
                    mb={1.5}
                    borderRadius="md"
                    sx={{ backgroundColor: "#f0f2f5" }}
                  >
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} sm={urlQrCk ? 6 : 12} sx={{ minWidth: 0 }}>
                        <Grid container rowSpacing={0.5} columnSpacing={1.5}>
                          <Don
                            nhan="Chủ tài khoản"
                            giaTri={chuVuon?.chuTaiKhoan || chuVuon?.hoTen}
                            xs={12}
                          />
                          <Don nhan="Ngân hàng" giaTri={chuVuon?.tenNganHang} xs={12} />
                        </Grid>
                        <Divider sx={{ my: 0.75 }} />
                        <HangSaoChep
                          nhan="Số tài khoản"
                          hienThi={chuVuon?.soTkNganHang ?? "—"}
                          giaTriCopy={chuVuon?.soTkNganHang ?? ""}
                          id="stk"
                          dangCopyId={daCopyId}
                          onSaoChep={saoChep}
                        />
                        <HangSaoChep
                          nhan="Số tiền"
                          hienThi={`${tongThanhTien.toLocaleString("vi-VN")} đ`}
                          giaTriCopy={tongThanhTien}
                          id="tien"
                          dangCopyId={daCopyId}
                          onSaoChep={saoChep}
                        />
                        <HangSaoChep
                          nhan="Nội dung"
                          hienThi={noiDungCk || "—"}
                          giaTriCopy={noiDungCk}
                          id="noidung"
                          dangCopyId={daCopyId}
                          onSaoChep={saoChep}
                        />
                      </Grid>
                      {urlQrCk && (
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                          <MDBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            sx={{ width: "100%", maxWidth: 315, mx: "auto" }}
                          >
                            <MDBox
                              component="img"
                              src={urlQrCk}
                              alt="QR chuyển khoản VietQR"
                              sx={{
                                width: "100%",
                                maxWidth: 315,
                                aspectRatio: "1 / 1",
                                height: "auto",
                                objectFit: "contain",
                                borderRadius: "8px",
                                backgroundColor: "#fff",
                                display: "block",
                              }}
                            />
                            <MDTypography
                              sx={{
                                mt: 0.75,
                                fontFamily: FONT_CHU,
                                fontSize: "0.75rem",
                                color: alpha(MAU.ink, 0.6),
                                textAlign: "center",
                              }}
                            >
                              Quét để chuyển khoản
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      )}
                    </Grid>
                  </MDBox>

                  {loi && (
                    <MDBox mb={1.5}>
                      <MDAlert color="error">{loi}</MDAlert>
                    </MDBox>
                  )}
                  <MDButton
                    id="tour-xac-nhan-nut"
                    variant="gradient"
                    color="info"
                    fullWidth
                    onClick={guiVeKeToan}
                    disabled={dangLuu}
                    sx={{ fontFamily: FONT_CHU, fontWeight: 600 }}
                  >
                    {dangLuu ? "Đang gửi..." : "Gửi về kế toán"}
                  </MDButton>
                </MDBox>
              </Card>
            )}

            {(daGuiKeToan || !conChoXuLy) && (
              <Card sx={{ borderRadius: "16px" }}>
                <MDBox p={2.5}>
                  {giaoDich.trangThaiThanhToan === "cho_ke_toan" || daGuiKeToan ? (
                    <MDAlert color="success">
                      Đã gửi phiếu về kế toán. Kế toán sẽ nhận thông tin và thanh toán — bạn không
                      cần tự xác nhận đã thanh toán.
                    </MDAlert>
                  ) : giaoDich.trangThaiThanhToan === "dang_thanh_toan" ? (
                    <MDAlert color="info">Kế toán đã nhận thông tin và đang thanh toán.</MDAlert>
                  ) : giaoDich.trangThaiThanhToan === "da_thanh_toan" ? (
                    <MDAlert color="success">Phiếu này đã được thanh toán.</MDAlert>
                  ) : (
                    <MDAlert color="info">Trạng thái: {nhanTrangThai.label}</MDAlert>
                  )}
                  {!laNguoiTao && conChoXuLy && (
                    <MDBox mt={1.5}>
                      <MDAlert color="warning">
                        Chỉ nhân viên tạo phiếu mới được gửi về kế toán.
                      </MDAlert>
                    </MDBox>
                  )}
                  {loi && (
                    <MDBox mt={1.5}>
                      <MDAlert color="error">{loi}</MDAlert>
                    </MDBox>
                  )}
                  {thongBaoChuong && (
                    <MDBox mt={1.5}>
                      <MDAlert color="info">{thongBaoChuong}</MDAlert>
                    </MDBox>
                  )}
                  {laNguoiTao && giaoDich.trangThaiThanhToan === "cho_ke_toan" && (
                    <MDBox mt={2}>
                      <MDButton
                        variant="gradient"
                        color="warning"
                        fullWidth
                        onClick={chuongNhacKeToan}
                        disabled={dangChuong}
                        startIcon={<Icon>notifications_active</Icon>}
                        sx={{ fontFamily: FONT_CHU, fontWeight: 600 }}
                      >
                        {dangChuong ? "Đang chuông..." : "Chuông nhắc kế toán"}
                      </MDButton>
                      <MDTypography variant="caption" color="text" display="block" mt={1}>
                        Dùng khi đợi lâu mà kế toán chưa nhận. Mỗi lần chuông cách nhau ít nhất 30
                        giây.
                      </MDTypography>
                    </MDBox>
                  )}
                  <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                    <MDButton
                      component={Link}
                      to={`/sot/${giaoDich.maSot}`}
                      variant="gradient"
                      color="info"
                    >
                      Xem Sọt
                    </MDButton>
                    <MDButton
                      component={Link}
                      to="/giao-dich-cua-toi"
                      variant="outlined"
                      color="dark"
                    >
                      Giao dịch của tôi
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default XacNhanThanhToan;
